import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Button from "../../../../../components/CustomButton";
import TextInput from "../../../../../components/TextInput";

// Context
import { useCoursesNewContext } from "../../context";

// Types
import { CourseData } from "../../types";

// Styles
import styles from "./style.module.scss";
import stepsStyles from "../steps.module.scss";
import TextField from "@mui/material/TextField";

interface BasicInfoProps {
  onNext: (courseData: CourseData) => void;
}

const BasicInfo: React.FC<BasicInfoProps> = ({ onNext }) => {
  // Translations
  const { t } = useTranslation();

  // Context
  const context = useCoursesNewContext();

  // State
  const [title, setTitle] = useState<string>(context.course?.title || "");
  const [description, setDescription] = useState<string>(context.course?.description || "");
  const [errors, setErrors] = useState<{ title: boolean }>({ title: false });

  // Memoized

  // Handlers

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setErrors({ title: false });
  }, []);

  const handleChangeDescription = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleNext = useCallback(() => {
    if (title.length < 3) {
      setErrors({ title: true });
      return;
    }

    onNext({ title, description });
  }, [title, description, onNext]);

  return (
    <div className={stepsStyles.root}>
      <div className={stepsStyles.content}>
        <h1 className='text-2xl font-bold text-black dark:text-white'>
          {t('courses.new.basic_info.title')}
        </h1>

        <div className={stepsStyles.formItem}>
          <label className={stepsStyles.label}>
            {t('courses.new.basic_info.subject_label')}

            <span className='text-blue-500'>*</span>
          </label>

          <TextField
            value={title}
            onChange={handleChangeTitle}
            classes={{ root: styles.input }}
            required={true}
            error={errors.title}
            helperText={errors.title ? t('courses.new.basic_info.title_error') : ""}
          />
        </div>

        <div className={styles.formItem}>
          <label className='text-md font-medium text-black dark:text-white'>
            {t('courses.new.basic_info.description_label')}
          </label>

          <textarea
            className={styles.textarea}
            value={description}
            onChange={handleChangeDescription}
          />
        </div>
      </div>

      <div className={stepsStyles.actions}>
        <div className={stepsStyles.prevButton} />
        <Button
          variant="primary"
          onClick={handleNext}
          classes={{ root: stepsStyles.nextButton }}
          disabled={errors.title || title.length < 3}
        >
          {t('actions.next')}
        </Button>
      </div>
    </div>
  );
};

export default BasicInfo;
